/* eslint-disable no-unused-vars */
/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react/default-props-match-prop-types */
import React, { ReactNode } from 'react';
import {
  FormControlLabelWrapper,
  FormControlLabelWrapperProps,
} from 'components/common/Form/Fields/FormField/FormField';
import { isString } from 'lodash';

type WithFormControlProps = Omit<FormControlLabelWrapperProps, 'children'> & {
  // Optional: the wrapper reads it as `!!error`, so absent has always been a valid state,
  // and the fields are given an error by a form rather than by the screen placing them. It
  // read as required only because the four `as any` spreads below skipped the check. [QW-323]
  error?: boolean | string;
  hideErrorHint?: boolean;
  FieldWrapper?: React.FunctionComponent<{ children: React.ReactNode }>;
  size?: string;
  placeholder?: string;
  split?: 'left' | 'right' | 'middle';
  adornmentPosition?: 'start' | 'end';
  testId?: string;
  adornment?: ReactNode;
  // The wrapper never reads `value` — it reads disabled, error, fullWidth, label, name and
  // required, then spreads the rest through. So this type constrains the fields it wraps on
  // behalf of a component that ignores the prop. Declared as `string` until QW-323, which is
  // the first TypeScript caller to pass a date to DatePicker.
  //
  // Widened rather than removed: the type is closed, so dropping `value` would make it an
  // illegal prop at the ten TypeScript call sites that pass one. The three other wrapped
  // fields are plain JavaScript and type their own values, so nothing here was checking them.
  value?: string | number | Date | object;
  // Props the WRAPPED field reads, which this type never listed. The wrapper is the only
  // thing a TypeScript caller can see, so until these were here no .tsx file could pass a
  // field its own props: the four QwireTrackAdmin filter panels each reach DatePicker
  // through `{...({ clearable: true, format: 'MM/DD/YYYY' } as any)}` for exactly this
  // reason, and those casts can now go. All eight belong to DatePicker except readOnly,
  // which InputText reads too. [QW-323]
  clearable?: boolean;
  format?: string;
  formatDate?: (date: Date | number, format: string) => string;
  minDate?: string | Date;
  popover?: boolean;
  readOnly?: boolean;
  tabIndex?: number;
  variant?: 'dialog' | 'inline' | 'static';
};

const withFormControl = (WrappedComponent) => {
  const Component = (props: WithFormControlProps) => {
    const { FieldWrapper, disabled, error, fullWidth, label, name, required, hideErrorHint } = props;
    const showError = !hideErrorHint && !!error;

    return (
      <FormControlLabelWrapper
        error={showError}
        helperText={isString(error) ? error : ''}
        data-testid="input-field"
        disabled={disabled}
        fullWidth={fullWidth}
        required={required}
        name={name}
        label={label}
      >
        {FieldWrapper ? (
          <FieldWrapper>
            <WrappedComponent {...props} />
          </FieldWrapper>
        ) : (
          <WrappedComponent {...props} />
        )}
      </FormControlLabelWrapper>
    );
  };

  /**
   * We're keeping this defaultProps to not break the current implementation of TypeAhead component,
   * handleChange function calls onChange(selected[valueKey]) and it is not safe to remove that now.
   * Once we replace the Typeahead by the MUI Autocomplete we can remove this.
   */
  Component.defaultProps = {
    onBlur: (_e: any) => undefined,
    onChange: (_e: any) => undefined,
    onFocus: (_e: any) => undefined,
  };

  Component.displayName = `withFormControl(${WrappedComponent.displayName})`;
  return Component;
};

export default withFormControl;
